import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Plus, Minus, ShoppingCart, Settings2, ChevronDown, Pencil } from 'lucide-react'
import { OrderItemCustomizations } from '@/types'
import { useMenuTheme } from '@/contexts/MenuThemeContext'

interface MenuItem {
  id: string
  name: string
  description?: string
  price: number
  imageUrl?: string
  categoryId: string
  available: boolean
  allergens?: string[]
  tags?: string[]
  customizationOptions?: any
}

interface ProductDetailModalProps {
  product: MenuItem | null
  isOpen: boolean
  onClose: () => void
  onAddToCart: (product: MenuItem, quantity: number, customizations?: OrderItemCustomizations | null) => void
  onCustomize?: (product: MenuItem) => void
}

// Compact allergen display
const ALLERGEN_DATA: Record<string, { icon: string; short: string }> = {
  'Glutine': { icon: '🌾', short: 'Glu' },
  'Latte': { icon: '🥛', short: 'Lat' },
  'Uova': { icon: '🥚', short: 'Uov' },
  'Pesce': { icon: '🐟', short: 'Pes' },
  'Crostacei': { icon: '🦐', short: 'Cro' },
  'Frutta a guscio': { icon: '🌰', short: 'Noc' },
  'Arachidi': { icon: '🥜', short: 'Ara' },
  'Soia': { icon: '🫘', short: 'Soi' },
  'Sedano': { icon: '🥬', short: 'Sed' },
  'Senape': { icon: '🟡', short: 'Sen' },
  'Sesamo': { icon: '🌱', short: 'Ses' },
  'Solfiti': { icon: '⚗️', short: 'Sol' },
  'Lupini': { icon: '🫛', short: 'Lup' },
  'Molluschi': { icon: '🦪', short: 'Mol' }
}

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product,
  isOpen,
  onClose,
  onAddToCart,
  onCustomize
}) => {
  const { theme } = useMenuTheme()
  const [quantity, setQuantity] = useState(1)
  const [notes, setNotes] = useState('')
  const [showNotes, setShowNotes] = useState(false)

  // Reset state when modal opens with new product
  React.useEffect(() => {
    if (isOpen) {
      setQuantity(1)
      setNotes('')
      setShowNotes(false)
    }
  }, [isOpen, product?.id])

  if (!isOpen || !product) return null

  const hasCustomizations = product.customizationOptions &&
    product.customizationOptions.optionGroups &&
    product.customizationOptions.optionGroups.length > 0

  const handleAddToCart = () => {
    if (hasCustomizations && onCustomize) {
      onCustomize(product)
      onClose()
    } else {
      const customizations: OrderItemCustomizations | null = notes.trim()
        ? { selectedOptions: [], freeTextNotes: notes.trim(), totalPriceModifier: 0 }
        : null
      onAddToCart(product, quantity, customizations)
      onClose()
    }
  }

  const totalPrice = product.price * quantity

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 top-[5%] sm:top-[10%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-lg md:bottom-auto md:max-h-[85vh] rounded-t-3xl md:rounded-3xl z-50 flex flex-col overflow-hidden"
            style={{
              backgroundColor: theme.cardBackground,
              borderColor: theme.borderColor,
              borderWidth: '1px',
              borderRadius: theme.cardBorderRadius,
            }}
          >
            {/* Image Header */}
            <div
              className="relative aspect-[4/3] max-h-72 shrink-0 overflow-hidden"
              style={{ backgroundColor: theme.backgroundColor }}
            >
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ background: theme.cardGradient }}>
                  <span className="text-7xl opacity-30">🍽️</span>
                </div>
              )}

              {/* Soft vignette for better text readability */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(to top, ${theme.cardBackground} 0%, ${theme.cardBackground}33 30%, transparent 100%)`
                }}
              />

              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <X size={20} style={{ color: theme.textColor }} />
              </button>

              {/* Unavailable badge */}
              {!product.available && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm">
                    Non Disponibile
                  </span>
                </div>
              )}

              {/* Badges row */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                {/* Customizable badge */}
                {hasCustomizations && (
                  <span
                    className="inline-flex items-center gap-1.5 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full"
                    style={{ backgroundColor: `${theme.primaryColor}e6` }}
                  >
                    <Settings2 size={12} />
                    Personalizzabile
                  </span>
                )}

                {/* Allergens preview - compact icons on image */}
                {product.allergens && product.allergens.length > 0 && (
                  <div className="flex items-center gap-0.5 bg-black/50 backdrop-blur-sm rounded-full px-2 py-1 ml-auto">
                    {product.allergens.slice(0, 4).map((allergen) => (
                      <span key={allergen} className="text-sm" title={allergen}>
                        {ALLERGEN_DATA[allergen]?.icon || '⚠️'}
                      </span>
                    ))}
                    {product.allergens.length > 4 && (
                      <span className="text-[10px] ml-0.5" style={{ color: `${theme.textColor}b3` }}>
                        +{product.allergens.length - 4}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-5">
              {/* Title & Price */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="text-xl font-bold" style={{ color: theme.textColor }}>
                  {product.name}
                </h2>
                <span className="text-xl font-bold shrink-0" style={{ color: theme.primaryColor }}>
                  €{product.price.toFixed(2)}
                </span>
              </div>

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="flex gap-1.5 flex-wrap mb-3">
                  {product.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-full text-xs"
                      style={{
                        backgroundColor: `${theme.textColor}0d`,
                        color: theme.textSecondary
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Description */}
              {product.description && (
                <p className="text-[15px] leading-relaxed mb-4" style={{ color: theme.textSecondary }}>
                  {product.description}
                </p>
              )}

              {/* Allergens section */}
              {product.allergens && product.allergens.length > 0 && (
                <div className="mb-4">
                  <p className="text-[11px] text-amber-400/70 font-medium uppercase tracking-wide mb-1.5 flex items-center gap-1">
                    <span>⚠️</span> Allergeni
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {product.allergens.map(allergen => (
                      <span
                        key={allergen}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded text-[11px] font-medium"
                      >
                        {ALLERGEN_DATA[allergen]?.icon || '⚠️'} {allergen}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Info about customizations */}
              {hasCustomizations && (
                <div
                  className="mb-4 rounded-xl p-3"
                  style={{
                    backgroundColor: `${theme.primaryColor}1a`,
                    borderColor: `${theme.primaryColor}33`,
                    borderWidth: '1px'
                  }}
                >
                  <p className="text-sm" style={{ color: theme.primaryColor }}>
                    Questo prodotto ha opzioni di personalizzazione.
                  </p>
                </div>
              )}

              {/* Notes - Collapsible (only for non-customizable) */}
              {!hasCustomizations && (
                <div>
                  <button
                    onClick={() => setShowNotes(!showNotes)}
                    className="flex items-center gap-2 transition-colors"
                    style={{ color: theme.mutedColor }}
                  >
                    <Pencil size={14} />
                    <span className="text-sm">
                      {notes ? 'Modifica note' : 'Aggiungi note'}
                    </span>
                    <motion.div
                      animate={{ rotate: showNotes ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown size={14} />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {showNotes && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Es: Senza cipolla, ben cotto..."
                          rows={2}
                          autoFocus
                          className="w-full mt-2 px-3 py-2 rounded-xl text-sm resize-none focus:outline-none transition-colors"
                          style={{
                            backgroundColor: `${theme.textColor}0d`,
                            borderColor: theme.borderColor,
                            borderWidth: '1px',
                            color: theme.textColor,
                          }}
                          maxLength={150}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {!showNotes && notes && (
                    <p className="text-xs mt-1 truncate" style={{ color: theme.mutedColor }}>"{notes}"</p>
                  )}
                </div>
              )}
            </div>

            {/* Footer - Add to Cart */}
            <div
              className="p-4"
              style={{
                backgroundColor: theme.backgroundColor,
                borderTopColor: theme.borderColor,
                borderTopWidth: '1px'
              }}
            >
              {hasCustomizations ? (
                <button
                  onClick={handleAddToCart}
                  disabled={!product.available}
                  className="w-full py-3 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-30"
                  style={{
                    background: theme.buttonGradient,
                    boxShadow: `0 10px 25px -5px ${theme.shadowColor}`
                  }}
                >
                  <Settings2 size={18} />
                  Personalizza e Aggiungi
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  {/* Quantity selector */}
                  <div
                    className="flex items-center gap-2 rounded-xl px-2 py-1"
                    style={{ backgroundColor: `${theme.textColor}0d` }}
                  >
                    <button
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg transition-colors disabled:opacity-30"
                      style={{ backgroundColor: `${theme.textColor}1a` }}
                    >
                      <Minus size={16} style={{ color: theme.textColor }} />
                    </button>
                    <span className="w-8 text-center text-lg font-bold" style={{ color: theme.textColor }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(q => q + 1)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg transition-colors"
                      style={{ backgroundColor: `${theme.textColor}1a` }}
                    >
                      <Plus size={16} style={{ color: theme.textColor }} />
                    </button>
                  </div>

                  {/* Add button with total */}
                  <button
                    onClick={handleAddToCart}
                    disabled={!product.available}
                    className="flex-1 py-3 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-30"
                    style={{
                      background: theme.buttonGradient,
                      boxShadow: `0 10px 25px -5px ${theme.shadowColor}`
                    }}
                  >
                    <ShoppingCart size={18} />
                    <span>Aggiungi</span>
                    <span className="opacity-90">€{totalPrice.toFixed(2)}</span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
